import { ChangeDetectorRef, Component, NgZone, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AHSTripettoComponent } from '@nx-ahs/script-collector';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AiService } from '../../../core/services/ai.service';
import { SecurityService } from '../../../core/services/security.service';
import { UserSettingsService } from '../../../core/services/user-settings.service';
import { TranslationLanguage } from '../models/translation-language.interface';
import { TranslationRequest } from '../models/translation-request.interface';
import { ApplicationModel } from '@nx-ahs/shared/models';
import { EncounterService } from '../../../core/services/encounter.service';
import { Subscription } from 'rxjs';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { AppSpinnerComponent } from '@nx-ahs/shared/components';

@Component({
	selector: 'app-note-translation',
	templateUrl: './note-translation.component.html',
	styleUrl: './note-translation.component.scss',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FlexLayoutModule,
		MatFormFieldModule,
		MatSelectModule,
		MatInputModule,
		AppSpinnerComponent
	]
})
export class NoteTranslationComponent extends AHSTripettoComponent implements OnInit, OnDestroy {
	@Output()
	notesChanged = new EventEmitter<{ target: { value: string } }>();

	@Output()
	notesBlurred = new EventEmitter<void>();

	@Output()
	notesLanguageChanged = new EventEmitter<TranslationLanguage>();

	@Input()
	maxCharLength: number = 8000;

	@Input()
	isEncounterNotes: boolean = false;

	@Input()
	preloadedText: string = '';

	@Input()
	preloadedLanguageSelectionCode: string = '';

	@Input()
	notesFieldLabel: string = 'Notes';

	public translationLanguages: TranslationLanguage[] = [];
	public noteTranslationForm: FormGroup;
	public loadingTranslation: boolean = false;
	public loadingTranslationLanguages: boolean = false;
	public isEnglish: boolean = false;

	public currentLanguageCode = '';
	public previousLanguageCode = '';
	public previousNotes = '';
	public notesFieldTouched = false;

	private app: ApplicationModel;
	private appSubscription: Subscription;
	private encounterNoteTranslationRequestFieldId: number;

	constructor(
		private aiService: AiService,
		private formBuilder: FormBuilder,
		private changeDetectorRef: ChangeDetectorRef,
		private ngZone: NgZone,
		private userSettingsService: UserSettingsService,
		private securityService: SecurityService,
		private encounterService: EncounterService
	) {
		super();
	}

	ngOnInit(): void {
		this.loadingTranslationLanguages = true;

		this.appSubscription = this.securityService.appChanged.subscribe((app: ApplicationModel) => {
			this.app = app;
			this.encounterNoteTranslationRequestFieldId = this.app.application.clientConfig.security.userSettings.find(
				(x) => x.id == 'language'
			).encounterNoteTranslationRequestFieldId;
		});

		if (this.securityService.getCurrentApp()) {
			this.app = this.securityService.getCurrentApp();
			this.encounterNoteTranslationRequestFieldId = this.app.application.clientConfig.security.userSettings.find(
				(x) => x.id == 'language'
			).encounterNoteTranslationRequestFieldId;
		}

		this.updateScriptData(this);

		//console.log(this.preloadedText);
		//console.log(this.preloadedLanguageSelectionCode);

		if (this.encounterService.encounterIsActive && this.aiService.currentEncounterNoteLanguageCode) {
			this.preloadedLanguageSelectionCode = this.aiService.currentEncounterNoteLanguageCode;
		}

		if (this.encounterService.encounterIsActive && this.aiService.currentEncounterNoteTranslation) {
			this.preloadedText = this.aiService.currentEncounterNoteTranslation;
		}

		this.aiService
			.getTranslationLanguages()
			.toPromise()
			.then((languageResults) => {
				this.translationLanguages = languageResults;
				//console.log(this.translationLanguages);

				if (this.preloadedLanguageSelectionCode && this.preloadedLanguageSelectionCode != '') {
					this.buildForm().then(() => {
						const preloadedLanguage = this.translationLanguages.find(
							(lang) => lang.translationLanguageCode == this.preloadedLanguageSelectionCode
						);

						this.currentLanguageCode = this.preloadedLanguageSelectionCode;
						this.aiService.currentEncounterNoteLanguageCode = this.preloadedLanguageSelectionCode;
						this.previousLanguageCode = this.preloadedLanguageSelectionCode;

						this.noteTranslationForm.get('translationLanguage').setValue(preloadedLanguage);

						this.setTripettoValue('');

						if (this.preloadedText && this.preloadedText != '') {
							this.noteTranslationForm.get('notes').setValue(this.preloadedText);
						}

						const noteTranslationTripettoVal = {
							notes: this.noteTranslationForm.get('notes').value,
							translationLanguageCode:
								this.noteTranslationForm.get('translationLanguage').value.translationLanguageCode
						};
						this.setTripettoValue(JSON.stringify(noteTranslationTripettoVal));

						this.ngZone.run(() => {
							this.loadingTranslationLanguages = false;
							this.changeDetectorRef.markForCheck();
						});
					});
				} else {
					this.userSettingsService
						.GetUserLanguagePreference()
						.toPromise()
						.then((languagePreferenceResult) => {
							if (languagePreferenceResult) {
								//console.log(languagePreferenceResult);

								this.buildForm().then(() => {
									//set currentLanguageCode, previousLanguageCode, and the translationLanguage formField to the initial language
									//will be different if the user has a language preference in the user settings

									const preferenceLanguage = this.translationLanguages.find(
										(lang) =>
											lang.translationLanguageCode == languagePreferenceResult.selectionValue
									);

									this.currentLanguageCode = languagePreferenceResult.selectionValue;
									this.aiService.currentEncounterNoteLanguageCode =
										languagePreferenceResult.selectionValue;
									this.previousLanguageCode = languagePreferenceResult.selectionValue;

									this.noteTranslationForm.get('translationLanguage').setValue(preferenceLanguage);
									// set the encounter language that doesn't seem to get set anywhere else
									this.encounterService.encounterNoteLanguage = preferenceLanguage;

									this.setTripettoValue('');

									if (this.preloadedText && this.preloadedText != '') {
										this.noteTranslationForm.get('notes').setValue(this.preloadedText);
									}

									const noteTranslationTripettoVal = {
										notes: this.noteTranslationForm.get('notes').value,
										translationLanguageCode:
											this.noteTranslationForm.get('translationLanguage').value
												.translationLanguageCode
									};
									this.setTripettoValue(JSON.stringify(noteTranslationTripettoVal));

									this.ngZone.run(() => {
										this.loadingTranslationLanguages = false;
										this.changeDetectorRef.markForCheck();
									});
								});
							} else {
								this.buildForm().then(() => {
									//set currentLanguageCode, previousLanguageCode, and the translationLanguage formField to the initial language
									//will be different if the user has a language preference in the user settings
									this.currentLanguageCode = 'en';
									this.aiService.currentEncounterNoteLanguageCode = 'en';
									this.previousLanguageCode = 'en';

									const englishTranslationLanguage = this.translationLanguages.find(
										(lang) => lang.translationLanguageCode == 'en'
									);

									this.noteTranslationForm
										.get('translationLanguage')
										.setValue(englishTranslationLanguage);

									this.setTripettoValue('');

									if (this.preloadedText && this.preloadedText != '') {
										this.noteTranslationForm.get('notes').setValue(this.preloadedText);
									}

									const noteTranslationTripettoVal = {
										notes: this.noteTranslationForm.get('notes').value,
										translationLanguageCode:
											this.noteTranslationForm.get('translationLanguage').value
												.translationLanguageCode
									};
									this.setTripettoValue(JSON.stringify(noteTranslationTripettoVal));

									this.ngZone.run(() => {
										this.loadingTranslationLanguages = false;
										this.changeDetectorRef.markForCheck();
									});
								});
							}
						});
				}
			});
	}

	ngOnDestroy() {
		if (this.appSubscription) {
			this.appSubscription.unsubscribe();
		}
	}

	public onNoteLanguageChange(_event: MatSelectChange) {
		//console.log(event);

		this.loadingTranslation = true;

		const requestNotes = this.noteTranslationForm.get('notes').value;
		//this.previousNotes = requestNotes;

		if (
			requestNotes &&
			requestNotes != '' &&
			(this.notesFieldTouched ||
				this.noteTranslationForm.get('translationLanguage').value.translationLanguageCode !=
					this.previousLanguageCode)
		) {
			this.previousNotes = requestNotes;

			//build the translation request
			const translationReq = {} as TranslationRequest;

			translationReq.createdByEntityNameId = 1;
			translationReq.createdBy = this.securityService.getCurrentUser().userId;
			translationReq.createdDate = new Date();

			//set the entity and field info for the request
			if (this.isEncounterNotes) {
				translationReq.entityNameId = 6;
				translationReq.entityId = this.encounterService.currentEncounter.encounterId;
				translationReq.translationRequestFieldId = this.encounterNoteTranslationRequestFieldId;
			}

			const currentTranslationLanguage = this.translationLanguages.find(
				(lang) => lang.translationLanguageCode == this.currentLanguageCode
			);
			translationReq.originalTranslationLanguage = currentTranslationLanguage;
			translationReq.translationLanguage = this.noteTranslationForm.get('translationLanguage').value;

			translationReq.originalTranslationLanguageId = currentTranslationLanguage.translationLanguageId;
			translationReq.translationLanguageId =
				this.noteTranslationForm.get('translationLanguage').value.translationLanguageId;

			translationReq.originalText = requestNotes;

			//add the other fields of the translation request to the request object

			this.aiService
				.translateText(translationReq)
				.toPromise()
				.then((result) => {
					//console.log(result);
					this.noteTranslationForm.get('notes').setValue(result);

					this.changeDetectorRef.detectChanges();
					//this.notesFieldTouched = false;
					this.loadingTranslation = false;

					//this.setTripettoValue(result); THIS MIGHT BE REQUIRED REMOVED IT TO RELY ON THE NOTESCHANGED FUNCTION

					this.onNotesChanged({} as unknown as Event);
					this.notesFieldTouched = false;
				});
		} else {
			//console.log("returning notes to previous notes");

			this.noteTranslationForm.get('notes').setValue(this.previousNotes);
			this.previousNotes = requestNotes;

			this.onNotesChanged({} as unknown as Event);
			this.notesFieldTouched = false;

			this.loadingTranslation = false;
		}

		this.previousLanguageCode = this.currentLanguageCode;
		this.currentLanguageCode = this.noteTranslationForm.get('translationLanguage').value.translationLanguageCode;
		this.aiService.currentEncounterNoteLanguageCode = this.currentLanguageCode;

		this.notesLanguageChanged.emit(this.noteTranslationForm.get('translationLanguage').value);

		//console.log(this.previousLanguageCode);
		//console.log(this.currentLanguageCode);
		//console.log(this.previousNotes);
		//console.log(this.notesFieldTouched);
	}

	public onNotesChanged(_event: Event) {
		this.notesFieldTouched = true;

		const formEventVal = this.noteTranslationForm.get('notes').value;
		//console.log(formEventVal);
		this.aiService.currentEncounterNoteTranslation = formEventVal;

		//emmitted in this format so the call caddy component can handle this in onNotesChange()
		const formEvent = { target: { value: formEventVal } };
		//console.log(formEvent);

		const noteTranslationTripettoVal = {
			notes: formEventVal,
			translationLanguageCode: this.noteTranslationForm.get('translationLanguage').value.translationLanguageCode
		};
		this.setTripettoValue(JSON.stringify(noteTranslationTripettoVal));

		this.changeDetectorRef.detectChanges();
		this.notesChanged.emit(formEvent);
	}

	private async buildForm(): Promise<void> {
		this.noteTranslationForm = this.formBuilder.group({
			translationLanguage: ['', [Validators.required]],
			notes: ['']
		});
	}
}
